/*
  Flabom · Home (Figma "01 • Homepage", frames 22:29 desktop 1440 e 75:24 mobile 390).

  Comportamentos usados pelas seções sections/flabom-home-*.liquid:

  1. [data-fb-fade]        carrossel de slides empilhados (hero, campaign banner):
                           só um slide visível; prev/next e dots trocam o ativo.
  2. [data-fb-rail]        carrossel por scroll horizontal (editorial desktop e
                           todos os trilhos mobile): prev/next rolam uma "página",
                           os dots refletem/comandam a posição do scroll.
  3. [data-fb-tabs]        abas do "Compre por categoria": os 4 painéis são
                           renderizados no servidor e a troca é só de classe
                           (sem reload, sem fetch).
  4. [data-fb-fav]         coração de favorito. A loja não tem app de wishlist
                           (ver spec, "Decisões"), então o estado fica em
                           localStorage sob a chave `flabom:wishlist`.
  5. [data-fb-card]        bolinhas de cor do card (feedback Yron 15/09): hover
                           ou foco mostra a foto da variante daquela cor; clique
                           fixa a cor (foto, variante da sacola e link do card).

  Sem dependências: não importa nada de @theme/* pra poder ser carregado como
  módulo simples em qualquer uma das seções da home, isoladamente.
*/

(() => {
  const WISHLIST_KEY = 'flabom:wishlist';

  /* ---------------------------------------------------------------- utils */

  const readWishlist = () => {
    try {
      const raw = window.localStorage.getItem(WISHLIST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (ids) => {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
    } catch (error) {
      /* storage bloqueado (aba anônima / cookies off): favorito vira só visual */
    }
  };

  /* ------------------------------------------------- 1. fade (hero/banner) */

  const initFade = (root) => {
    if (root.dataset.fbFadeReady === 'true') return;
    root.dataset.fbFadeReady = 'true';

    const slides = Array.from(root.querySelectorAll('[data-fb-slide]'));
    if (slides.length === 0) return;

    const prev = root.querySelector('[data-fb-prev]');
    const next = root.querySelector('[data-fb-next]');
    const dots = Array.from(root.querySelectorAll('[data-fb-dot]'));
    let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (index < 0) index = 0;

    const show = (target) => {
      index = (target + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    };

    /* Auditoria A7: com um slide só, setas e bolinhas seriam controles mortos. */
    if (slides.length < 2) {
      [prev, next, ...dots].forEach((control) => control?.style.setProperty('display', 'none'));
      return;
    }

    prev?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));

    show(index);
  };

  /* ------------------------------------------------ 2. rail (scroll + dots) */

  const initRail = (root) => {
    if (root.dataset.fbRailReady === 'true') return;
    root.dataset.fbRailReady = 'true';

    const track = root.querySelector('[data-fb-track]');
    if (!track) return;

    const prev = root.querySelector('[data-fb-prev]');
    const next = root.querySelector('[data-fb-next]');
    const dots = Array.from(root.querySelectorAll('[data-fb-dot]'));

    const step = () => {
      const first = track.firstElementChild;
      if (!first) return track.clientWidth;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const scrollBy = (direction) => {
      track.scrollBy({ left: direction * step(), behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => scrollBy(-1));
    next?.addEventListener('click', () => scrollBy(1));

    const syncDots = () => {
      if (dots.length === 0) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      let active = 0;
      if (maxScroll > 1) {
        const ratio = track.scrollLeft / maxScroll;
        active = Math.round(ratio * (dots.length - 1));
      }
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    };

    const syncArrows = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      prev?.toggleAttribute('aria-disabled', track.scrollLeft <= 1);
      next?.toggleAttribute('aria-disabled', track.scrollLeft >= maxScroll - 1);
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        const ratio = dots.length > 1 ? i / (dots.length - 1) : 0;
        track.scrollTo({ left: maxScroll * ratio, behavior: 'smooth' });
      });
    });

    track.addEventListener('scroll', () => {
      syncDots();
      syncArrows();
    });

    syncDots();
    syncArrows();
  };

  /* ------------------------------------------------------------- 3. abas */

  const initTabs = (root) => {
    if (root.dataset.fbTabsReady === 'true') return;
    root.dataset.fbTabsReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-fb-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-fb-panel]'));
    if (tabs.length === 0 || panels.length === 0) return;

    const activate = (id) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.fbTab === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach((panel) => {
        const active = panel.dataset.fbPanel === id;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab.dataset.fbTab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const current = tabs.indexOf(tab);
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const target = tabs[(current + delta + tabs.length) % tabs.length];
        target.focus();
        activate(target.dataset.fbTab);
      });
    });

    const initial = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
    activate(initial.dataset.fbTab);
  };

  /* -------------------------------------------------------- 4. favoritos */

  const initFavorites = (scope) => {
    const buttons = Array.from(scope.querySelectorAll('[data-fb-fav]'));
    if (buttons.length === 0) return;

    const saved = readWishlist();

    buttons.forEach((button) => {
      if (button.dataset.fbFavReady === 'true') return;
      button.dataset.fbFavReady = 'true';

      const id = button.dataset.fbFav;
      const setState = (active) => {
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      };

      setState(saved.includes(id));

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const list = readWishlist();
        const position = list.indexOf(id);
        if (position >= 0) {
          list.splice(position, 1);
        } else {
          list.push(id);
        }
        writeWishlist(list);
        setState(position < 0);
      });
    });
  };

  /* ----------------------------------------------------- 5. cores do card */

  const initCardSwatches = (scope) => {
    scope.querySelectorAll('[data-fb-card]').forEach((card) => {
      if (card.dataset.fbCardReady === 'true') return;
      const swatches = Array.from(card.querySelectorAll('[data-fb-swatch-variant]'));
      if (swatches.length === 0) return;
      card.dataset.fbCardReady = 'true';

      const img = card.querySelector('.fb-home-pc__image');
      const list = card.querySelector('[data-fb-card-swatches]');
      const idInput = card.querySelector('input[name="id"]');
      const bag = card.querySelector('.fb-home-pc__bag');
      const links = Array.from(card.querySelectorAll('.fb-home-pc__image-link, .fb-home-pc__name'));
      const original = img ? { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') } : null;
      let selected = swatches.find((s) => s.getAttribute('aria-pressed') === 'true') || null;

      const paint = (swatch) => {
        if (!(img instanceof HTMLImageElement)) return;
        const src = swatch?.dataset.fbSwatchSrc || original?.src;
        const srcset = swatch?.dataset.fbSwatchSrcset || original?.srcset;
        if (!src || img.getAttribute('src') === src) return;
        if (srcset) img.setAttribute('srcset', srcset);
        else img.removeAttribute('srcset');
        img.setAttribute('src', src);
      };

      /* pré-carrega as fotos das cores na 1ª vez que o mouse entra no card */
      card.addEventListener(
        'pointerenter',
        () => {
          swatches.forEach((s) => {
            if (s.dataset.fbSwatchSrc) new Image().src = s.dataset.fbSwatchSrc;
          });
        },
        { once: true }
      );

      swatches.forEach((swatch) => {
        swatch.addEventListener('pointerenter', () => paint(swatch));
        swatch.addEventListener('focus', () => paint(swatch));
        swatch.addEventListener('click', (event) => {
          event.preventDefault();
          selected = swatch;
          swatches.forEach((s) => s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false'));
          paint(swatch);
          if (idInput instanceof HTMLInputElement) idInput.value = swatch.dataset.fbSwatchVariant || idInput.value;
          if (bag instanceof HTMLButtonElement) bag.disabled = swatch.dataset.fbSwatchAvailable !== 'true';
          if (swatch.dataset.fbSwatchUrl) links.forEach((a) => a.setAttribute('href', swatch.dataset.fbSwatchUrl));
        });
      });

      list?.addEventListener('pointerleave', () => paint(selected));
      list?.addEventListener('focusout', (event) => {
        if (!list.contains(/** @type {Node|null} */ (event.relatedTarget))) paint(selected);
      });
    });
  };

  /* ------------------------------------------------------------ bootstrap */

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-fb-fade]').forEach(initFade);
    scope.querySelectorAll('[data-fb-rail]').forEach(initRail);
    scope.querySelectorAll('[data-fb-tabs]').forEach(initTabs);
    initFavorites(scope);
    initCardSwatches(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  /* Theme editor: seção re-renderizada = re-inicializa só ela. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) boot(event.target);
  });
})();
